import { useEffect, useState, Fragment, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Users, Shield, CheckCircle, Clock, Calendar, UserCheck,
  ChevronDown, Phone, Mail, User, Edit2, Save, X, FileText,
  MessageSquare, AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CoachTeamPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  const [activeTab, setActiveTab] = useState('roster');
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editForm, setEditForm] = useState({ jersey_number: '', position: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [paperwork, setPaperwork] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteForm, setNoteForm] = useState({ player_id: '', category: '', body: '' });
  const [postingNote, setPostingNote] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMyTeams();
  }, []);

  const fetchMyTeams = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/coach/my-teams`, { withCredentials: true });
      setTeams(response.data);
      if (response.data.length > 0) setSelectedTeam(response.data[0]);
    } catch (e) {
      console.error('Failed to fetch teams:', e);
    } finally {
      setLoading(false);
    }
  };

  const isAdminRole = ['super_admin', 'league_admin'].includes(user?.role);
  const staffRole = selectedTeam?.staff_role; // 'head_coach' | 'assistant_coach' | 'team_mom' | null
  const canEditAssignment = isAdminRole || ['head_coach', 'assistant_coach'].includes(staffRole);
  const canPostCoachingNote = isAdminRole || ['head_coach', 'assistant_coach'].includes(staffRole);
  const canPostPaperworkNote = isAdminRole || staffRole === 'team_mom';

  const refreshTeam = useCallback(async () => {
    if (!selectedTeam) return;
    const r = await axios.get(`${API_URL}/api/coach/my-teams`, { withCredentials: true });
    setTeams(r.data);
    const same = r.data.find(t => t.team_id === selectedTeam.team_id);
    if (same) setSelectedTeam(same);
  }, [selectedTeam]);

  const fetchPaperwork = useCallback(async (teamId) => {
    try {
      const r = await axios.get(`${API_URL}/api/coach/team/${teamId}/paperwork-status`, { withCredentials: true });
      setPaperwork(r.data || []);
    } catch (e) {
      setPaperwork([]);
    }
  }, []);

  const fetchNotes = useCallback(async (teamId) => {
    try {
      const r = await axios.get(`${API_URL}/api/coach/team/${teamId}/notes`, { withCredentials: true });
      setNotes(r.data || []);
    } catch (e) {
      setNotes([]);
    }
  }, []);

  useEffect(() => {
    if (!selectedTeam?.team_id) return;
    if (activeTab === 'paperwork') fetchPaperwork(selectedTeam.team_id);
    if (activeTab === 'notes') fetchNotes(selectedTeam.team_id);
  }, [activeTab, selectedTeam, fetchPaperwork, fetchNotes]);

  const startEdit = (player) => {
    setEditingPlayer(player.player_id);
    setEditForm({ jersey_number: player.jersey_number || '', position: player.position || '' });
    setError('');
  };

  const cancelEdit = () => {
    setEditingPlayer(null);
    setEditForm({ jersey_number: '', position: '' });
    setError('');
  };

  const saveEdit = async (playerId) => {
    if (!selectedTeam) return;
    setSavingEdit(true);
    setError('');
    try {
      await axios.put(
        `${API_URL}/api/coach/team/${selectedTeam.team_id}/player/${playerId}/assignment`,
        { jersey_number: editForm.jersey_number, position: editForm.position },
        { withCredentials: true }
      );
      await refreshTeam();
      cancelEdit();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingEdit(false);
    }
  };

  const postNote = async () => {
    if (!selectedTeam || !noteForm.player_id || !noteForm.category || !noteForm.body.trim()) {
      setError('Player, category, and body are required');
      return;
    }
    setPostingNote(true);
    setError('');
    try {
      await axios.post(
        `${API_URL}/api/coach/team/${selectedTeam.team_id}/player/${noteForm.player_id}/note`,
        { category: noteForm.category, body: noteForm.body.trim() },
        { withCredentials: true }
      );
      setNoteForm({ player_id: '', category: '', body: '' });
      await fetchNotes(selectedTeam.team_id);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to post note');
    } finally {
      setPostingNote(false);
    }
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-gray-200 rounded-lg w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  if (teams.length === 0) {
    return (
      <ParentLayout>
        <div className="max-w-7xl mx-auto px-4 py-8 text-center">
          <Shield className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 text-lg">No teams assigned</p>
          <p className="text-gray-400 text-sm mt-2">Contact league administration to be assigned to a team.</p>
        </div>
      </ParentLayout>
    );
  }

  const clearedPlayers = selectedTeam?.roster?.filter(p => p.clearance_status === 'cleared') || [];
  const pendingPlayers = selectedTeam?.roster?.filter(p => p.clearance_status !== 'cleared') || [];

  return (
    <ParentLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="coach-team-page">
        {/* Team Selector */}
        {teams.length > 1 && (
          <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
            {teams.map(team => (
              <button
                key={team.team_id}
                onClick={() => setSelectedTeam(team)}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                  selectedTeam?.team_id === team.team_id
                    ? 'bg-navy text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {team.name}
              </button>
            ))}
          </div>
        )}

        {/* Team Header */}
        <div className="bg-gradient-to-r from-red to-red/90 rounded-xl p-6 mb-6 text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center space-x-4">
              {selectedTeam?.logo_url ? (
                <div className="w-20 h-20 flex items-center justify-center flex-shrink-0">
                  <img 
                    src={selectedTeam.logo_url} 
                    alt={`${selectedTeam.name} logo`}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                  <Shield className="w-8 h-8 text-gold" />
                </div>
              )}
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">{selectedTeam?.name}</h1>
                <p className="text-white/80">{selectedTeam?.division_name}</p>
              </div>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-6">
              <div className="text-center">
                <p className="text-3xl font-bold">{selectedTeam?.roster_count || 0}</p>
                <p className="text-sm text-white/70">Players</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-300">{clearedPlayers.length}</p>
                <p className="text-sm text-white/70">Cleared</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-yellow-300">{pendingPlayers.length}</p>
                <p className="text-sm text-white/70">Pending</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col items-center"
            onClick={() => navigate('/schedule')}
            data-testid="quick-action-schedule"
          >
            <Calendar className="w-6 h-6 mb-2 text-navy" />
            <span className="text-sm">Schedule</span>
          </Button>
          {/* Per-team attendance dashboard not yet built; per-event attendance
              now lives on each EventDetailPage's Guest List. Hidden for now. */}
          {false && (
          <Button 
            variant="outline" 
            className="h-auto py-4 flex flex-col items-center"
            onClick={() => navigate(`/coach/team/${selectedTeam?.team_id}/attendance`)}
          >
            <UserCheck className="w-6 h-6 mb-2 text-navy" />
            <span className="text-sm">Attendance</span>
          </Button>
          )}
          {staffRole && (
            <div className="col-span-2 sm:col-span-3 flex items-center justify-end">
              <Badge className="bg-navy text-white capitalize" data-testid="staff-role-badge">
                <Shield className="w-3 h-3 mr-1" />
                {String(staffRole).replace('_', ' ')}
              </Badge>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-4 border-b" data-testid="coach-tabs">
          <button
            onClick={() => setActiveTab('roster')}
            data-testid="tab-roster"
            className={`px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'roster' ? 'text-navy border-b-2 border-navy' : 'text-gray-500 hover:text-navy'}`}
          >
            <Users className="w-4 h-4 inline mr-1" /> Roster
          </button>
          {(staffRole === 'team_mom' || isAdminRole) && (
            <button
              onClick={() => setActiveTab('paperwork')}
              data-testid="tab-paperwork"
              className={`px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'paperwork' ? 'text-navy border-b-2 border-navy' : 'text-gray-500 hover:text-navy'}`}
            >
              <FileText className="w-4 h-4 inline mr-1" /> Paperwork
            </button>
          )}
          <button
            onClick={() => setActiveTab('notes')}
            data-testid="tab-notes"
            className={`px-4 py-2 text-sm font-semibold transition-colors ${activeTab === 'notes' ? 'text-navy border-b-2 border-navy' : 'text-gray-500 hover:text-navy'}`}
          >
            <MessageSquare className="w-4 h-4 inline mr-1" /> Notes
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-center" data-testid="coach-error">
            <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* ROSTER TAB */}
        {activeTab === 'roster' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-navy">Team Roster</CardTitle>
            <Badge className="bg-navy text-white">
              {selectedTeam?.roster_count || 0} Players
            </Badge>
          </CardHeader>
          <CardContent>
            {selectedTeam?.roster?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">#</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">Player</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-gray-600 hidden sm:table-cell">Position</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-gray-600 hidden sm:table-cell">Age</th>
                      <th className="text-left py-3 px-2 text-sm font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTeam.roster.map(player => {
                      const expanded = expandedPlayer === player.player_id;
                      const hasContact = player.parent_name || player.parent_phone || player.parent_email;
                      return (
                      <Fragment key={player.player_id}>
                      <tr 
                        className="border-b hover:bg-gray-50"
                        data-testid={`player-row-${player.player_id}`}
                      >
                        <td className="py-3 px-2">
                          {editingPlayer === player.player_id ? (
                            <Input
                              value={editForm.jersey_number}
                              onChange={(e) => setEditForm(f => ({ ...f, jersey_number: e.target.value }))}
                              className="w-16 h-9 text-center font-bold"
                              maxLength={4}
                              data-testid={`edit-jersey-${player.player_id}`}
                              autoFocus
                            />
                          ) : (
                            <div
                              className="w-8 h-8 bg-navy rounded-full flex items-center justify-center text-white font-bold text-sm cursor-pointer"
                              onClick={() => hasContact && setExpandedPlayer(expanded ? null : player.player_id)}
                            >
                              {player.jersey_number || '?'}
                            </div>
                          )}
                        </td>
                        <td
                          className="py-3 px-2 cursor-pointer"
                          onClick={() => editingPlayer !== player.player_id && hasContact && setExpandedPlayer(expanded ? null : player.player_id)}
                        >
                          <p className="font-medium text-navy flex items-center gap-1">
                            {player.first_name} {player.last_name}
                            {hasContact && editingPlayer !== player.player_id && (
                              <ChevronDown
                                className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
                              />
                            )}
                          </p>
                          <p className="text-xs text-gray-500 sm:hidden">{player.position || 'TBD'}</p>
                        </td>
                        <td className="py-3 px-2 hidden sm:table-cell text-gray-600">
                          {editingPlayer === player.player_id ? (
                            <Input
                              value={editForm.position}
                              onChange={(e) => setEditForm(f => ({ ...f, position: e.target.value }))}
                              className="w-28 h-9"
                              maxLength={40}
                              data-testid={`edit-position-${player.player_id}`}
                            />
                          ) : (
                            player.position || 'TBD'
                          )}
                        </td>
                        <td className="py-3 px-2 hidden sm:table-cell text-gray-600">
                          {player.age_group}
                        </td>
                        <td className="py-3 px-2">
                          {editingPlayer === player.player_id ? (
                            <div className="flex items-center space-x-1">
                              <Button
                                size="sm"
                                onClick={() => saveEdit(player.player_id)}
                                disabled={savingEdit}
                                className="bg-navy hover:bg-navy/90 h-8"
                                data-testid={`save-edit-${player.player_id}`}
                              >
                                <Save className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-8">
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <Badge className={
                                player.clearance_status === 'cleared' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }>
                                {player.clearance_status === 'cleared' ? (
                                  <><CheckCircle className="w-3 h-3 mr-1" /> Cleared</>
                                ) : (
                                  <><Clock className="w-3 h-3 mr-1" /> {player.clearance_reason || 'Pending'}</>
                                )}
                              </Badge>
                              {canEditAssignment && (
                                <button
                                  type="button"
                                  onClick={() => startEdit(player)}
                                  className="text-gray-400 hover:text-navy"
                                  data-testid={`edit-player-${player.player_id}`}
                                  title="Edit jersey & position"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                      {expanded && hasContact && (
                        <tr
                          className="bg-gray-50 border-b"
                          data-testid={`player-contact-${player.player_id}`}
                        >
                          <td colSpan={5} className="py-3 px-4">
                            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                              {player.parent_name && (
                                <span className="flex items-center gap-1.5 text-gray-700">
                                  <User className="w-3.5 h-3.5 text-gray-400" />
                                  <span className="font-medium">{player.parent_name}</span>
                                </span>
                              )}
                              {player.parent_phone && (
                                <a
                                  href={`tel:${player.parent_phone}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="flex items-center gap-1.5 text-navy hover:underline"
                                  data-testid={`contact-phone-${player.player_id}`}
                                >
                                  <Phone className="w-3.5 h-3.5" />
                                  {player.parent_phone}
                                </a>
                              )}
                              {player.parent_email && (
                                <a
                                  href={`mailto:${player.parent_email}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="flex items-center gap-1.5 text-navy hover:underline"
                                  data-testid={`contact-email-${player.player_id}`}
                                >
                                  <Mail className="w-3.5 h-3.5" />
                                  {player.parent_email}
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No players on roster</p>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Pending Clearance Alert */}
        {activeTab === 'roster' && pendingPlayers.length > 0 && (
          <Card className="mt-6 border-yellow-200 bg-yellow-50">
            <CardHeader>
              <CardTitle className="text-yellow-800 flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                Players Pending Clearance ({pendingPlayers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-yellow-700 mb-3">
                These players have outstanding requirements and cannot participate until cleared.
              </p>
              <div className="flex flex-wrap gap-2">
                {pendingPlayers.map(player => (
                  <Badge key={player.player_id} variant="secondary" className="bg-yellow-100 text-yellow-800">
                    {player.first_name} {player.last_name}{player.clearance_reason ? ` — ${player.clearance_reason}` : ''}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* PAPERWORK TAB */}
        {activeTab === 'paperwork' && (
          <Card data-testid="paperwork-card">
            <CardHeader>
              <CardTitle className="text-navy">Paperwork Status</CardTitle>
              <p className="text-xs text-gray-500">Status only — contact admin for details, document content, or payment amounts.</p>
            </CardHeader>
            <CardContent>
              {paperwork.length === 0 ? (
                <p className="text-sm text-gray-500 py-4">No players on this team's roster.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium text-gray-600">Player</th>
                      <th className="text-left py-2 px-2 font-medium text-gray-600">Documents</th>
                      <th className="text-left py-2 px-2 font-medium text-gray-600">Waiver</th>
                      <th className="text-left py-2 px-2 font-medium text-gray-600">Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paperwork.map(row => (
                      <tr key={row.player_id} className="border-b" data-testid={`paperwork-row-${row.player_id}`}>
                        <td className="py-2 px-2 font-medium text-navy">{row.name}</td>
                        <td className="py-2 px-2">
                          <Badge className={row.documents_status === 'complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                            {row.documents_status}
                          </Badge>
                        </td>
                        <td className="py-2 px-2">
                          <Badge className={row.waiver_status === 'signed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                            {row.waiver_status}
                          </Badge>
                        </td>
                        <td className="py-2 px-2">
                          <Badge className={['confirmed', 'paid'].includes(row.payment_status) ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                            {row.payment_status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        )}

        {/* NOTES TAB */}
        {activeTab === 'notes' && (
          <div className="space-y-4" data-testid="notes-section">
            {(canPostCoachingNote || canPostPaperworkNote) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-navy text-base">Add a note</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-600">Player</label>
                      <select
                        value={noteForm.player_id}
                        onChange={(e) => setNoteForm(f => ({ ...f, player_id: e.target.value }))}
                        className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                        data-testid="note-player-select"
                      >
                        <option value="">Select player…</option>
                        {(selectedTeam?.roster || []).map(p => (
                          <option key={p.player_id} value={p.player_id}>{p.first_name} {p.last_name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Category</label>
                      <select
                        value={noteForm.category}
                        onChange={(e) => setNoteForm(f => ({ ...f, category: e.target.value }))}
                        className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                        data-testid="note-category-select"
                      >
                        <option value="">Select category…</option>
                        {canPostCoachingNote && <option value="coaching">Coaching</option>}
                        {canPostCoachingNote && <option value="injury">Injury</option>}
                        {canPostCoachingNote && <option value="other">Other</option>}
                        {canPostPaperworkNote && <option value="paperwork">Paperwork</option>}
                        {canPostPaperworkNote && <option value="payment_concern">Payment concern</option>}
                        {canPostPaperworkNote && <option value="communication">Communication</option>}
                      </select>
                    </div>
                  </div>
                  <Textarea
                    value={noteForm.body}
                    onChange={(e) => setNoteForm(f => ({ ...f, body: e.target.value }))}
                    rows={3}
                    placeholder="Add details…"
                    maxLength={2000}
                    data-testid="note-body"
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={postNote}
                      disabled={postingNote || !noteForm.player_id || !noteForm.category || !noteForm.body.trim()}
                      className="bg-navy hover:bg-navy/90"
                      data-testid="post-note-btn"
                    >
                      {postingNote ? 'Posting…' : 'Post Note'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardHeader>
                <CardTitle className="text-navy text-base">Team Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {notes.length === 0 ? (
                  <p className="text-sm text-gray-500 py-2">No notes yet.</p>
                ) : (
                  notes.map(n => {
                    const player = (selectedTeam?.roster || []).find(p => p.player_id === n.player_id);
                    return (
                      <div
                        key={n.note_id}
                        className="border rounded-lg p-3 bg-gray-50"
                        data-testid={`note-${n.note_id}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center space-x-2">
                            <Badge className="bg-navy text-white capitalize">{n.category.replace('_', ' ')}</Badge>
                            <span className="text-sm font-medium text-navy">
                              {player ? `${player.first_name} ${player.last_name}` : n.player_id}
                            </span>
                          </div>
                          <Badge variant={n.status === 'resolved' ? 'outline' : 'secondary'} className={n.status === 'resolved' ? 'border-green-500 text-green-700' : 'bg-yellow-100 text-yellow-800'}>
                            {n.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{n.body}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {n.author_name || n.author_role} — {new Date(n.created_at).toLocaleString()}
                        </p>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </ParentLayout>
  );
};

export default CoachTeamPage;
